// applicantAdult — the shared "is this applicant an adult?" heuristic, kept free
// of `./api` (and its Vite-only `import.meta.env`) so it stays unit-testable
// under node:test/tsx. Same reason leaseholderChangeMatch lives on its own.
//
// Every join document needs it for the same reason: minors on an approved
// application join the household as OCCUPANTS, not leaseholders, so they must
// never be pre-selected as a signer.

/** The two signals AppFolio may carry about an applicant's age. */
export interface ApplicantAgeSignals {
  /** AppFolio's adult flag; null when the view doesn't expose it. */
  isAdult: boolean | null;
  /** yyyy-mm-dd; null when AppFolio has no DOB on file. */
  dateOfBirth: string | null;
}

/**
 * Whether an approved-group applicant should be treated as an adult (and so
 * pre-selected as a signing party): AppFolio says adult, or (no flag) the DOB
 * implies ≥18, or BOTH signals are missing (assume adult — staff review the rows
 * anyway). A KNOWN minor returns false and is shown excluded with a note.
 */
export function applicantLikelyAdult(
  a: ApplicantAgeSignals,
  today: Date = new Date(),
): boolean {
  if (a.isAdult === true) return true;
  if (a.isAdult === false) return false;
  const dob = (a.dateOfBirth ?? '').trim();
  if (!dob) return true; // both signals missing → assume adult
  const d = new Date(`${dob}T00:00:00`);
  if (Number.isNaN(d.getTime())) return true;
  let age = today.getFullYear() - d.getFullYear();
  const beforeBirthday =
    today.getMonth() < d.getMonth() ||
    (today.getMonth() === d.getMonth() && today.getDate() < d.getDate());
  if (beforeBirthday) age -= 1;
  return age >= 18;
}
